import React, { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Send, Plus, Loader2, Trash2, Bot, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { api, API, TOKEN_KEY, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

const SUGGESTIONS = [
  "What's my net cash position and how can I improve it?",
  "Draft a follow-up plan for my open prospects.",
  "Which invoices are still outstanding?",
  "Give me 3 ideas to grow revenue this month.",
];

export default function AIHub() {
  const qc = useQueryClient();
  const { refreshUser } = useAuth();
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  const { data: conversations = [] } = useQuery({ queryKey: ["conversations"], queryFn: async () => (await api.get("/ai/conversations")).data });

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, streaming]);

  const loadConv = async (id) => {
    setConvId(id);
    const { data } = await api.get(`/ai/conversations/${id}`);
    setMessages(data.messages || []);
  };
  const newChat = () => { setConvId(null); setMessages([]); };

  const deleteConv = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/ai/conversations/${id}`);
    if (id === convId) newChat();
    qc.invalidateQueries({ queryKey: ["conversations"] });
  };

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const resp = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
        body: JSON.stringify({ conversation_id: convId, message }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(formatApiErrorDetail(err.detail) || "Request failed");
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let newConvId = convId;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          let evt;
          try { evt = JSON.parse(line); } catch { continue; }
          if (evt.type === "meta") { newConvId = evt.conversation_id; if (!convId) setConvId(evt.conversation_id); }
          else if (evt.type === "delta") {
            setMessages((m) => { const copy = [...m]; copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + evt.content }; return copy; });
          } else if (evt.type === "error") {
            setMessages((m) => { const copy = [...m]; copy[copy.length - 1] = { role: "assistant", content: "⚠️ " + evt.content }; return copy; });
          }
        }
      }
      qc.invalidateQueries({ queryKey: ["conversations"] });
      refreshUser();
    } catch (err) {
      toast.error(err.message);
      setMessages((m) => { const copy = [...m]; if (copy.length && !copy[copy.length - 1].content) copy[copy.length - 1] = { role: "assistant", content: "⚠️ " + err.message }; return copy; });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-9rem)] gap-4 lg:h-[calc(100vh-11rem)]">
      {/* Conversations */}
      <div className="hidden w-64 shrink-0 flex-col rounded-2xl border border-border bg-card p-3 md:flex">
        <Button onClick={newChat} className="w-full" data-testid="new-chat-button"><Plus className="mr-2 h-4 w-4" /> New chat</Button>
        <div className="mt-3 flex-1 space-y-1 overflow-y-auto">
          {conversations.map((c) => (
            <div key={c.id} onClick={() => loadConv(c.id)} data-testid={`conversation-${c.id}`}
              className={cn("group flex cursor-pointer items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors", convId === c.id ? "bg-secondary" : "hover:bg-secondary/60")}>
              <span className="truncate">{c.title}</span>
              <button onClick={(e) => deleteConv(c.id, e)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive" data-testid={`delete-conversation-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Chat */}
      <div className="flex flex-1 flex-col rounded-2xl border border-border bg-card">
        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-6" data-testid="chat-messages">
          {messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/10 text-accent"><Sparkles className="h-7 w-7" /></div>
              <h2 className="mt-4 font-display text-xl font-bold">Ask your business anything</h2>
              <p className="mt-1.5 max-w-sm text-sm text-muted-foreground">Your AI co-pilot knows your revenue, contacts, tasks, and more.</p>
              <div className="mt-6 grid w-full max-w-md gap-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} className="rounded-xl border border-border p-3 text-left text-sm transition-colors hover:bg-secondary" data-testid="chat-suggestion">{s}</button>
                ))}
              </div>
            </div>
          ) : messages.map((m, i) => (
            <div key={i} className={cn("flex gap-3", m.role === "user" ? "flex-row-reverse" : "")} data-testid={`chat-message-${m.role}`}>
              <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", m.role === "user" ? "bg-primary text-primary-foreground" : "bg-accent/15 text-accent")}>
                {m.role === "user" ? <UserIcon className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
              </div>
              <div className={cn("max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed", m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary")}>
                {m.content ? <span className="whitespace-pre-wrap">{m.content}</span> : <Loader2 className="h-4 w-4 animate-spin" />}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-border p-3 sm:p-4">
          <div className="flex items-end gap-2">
            <Textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Message BizForge AI..." rows={1} className="min-h-[44px] resize-none" data-testid="chat-input" />
            <Button size="icon" onClick={() => send()} disabled={streaming || !input.trim()} className="h-11 w-11 shrink-0" data-testid="chat-send-button">
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
